import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API.js'
import { GlobalContext, TenantSettingsContext } from '../../../../App.js'

// Site Config → Governance → Speak Up. On by default; this is the org's
// opt-out and the place to add local guidance (guardian's name, helplines).
const SpeakUpSettings = () => {
  const [loading, setLoading] = useState(true)
  const [changesPresent, setChangesPresent] = useState(false)
  const [enabled, setEnabled] = useState(true)
  const [intro, setIntro] = useState('')

  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  useEffect(() => {
    API.get('/settings?select=speakUpSettings').then((res) => {
      if (res.status === 200) {
        const settings = res.data?.speakUpSettings ?? {}
        setEnabled(settings.enabled !== false)
        setIntro(settings.intro ?? '')
      }
      setLoading(false)
    })
  }, [])

  const handleSaveChanges = () => {
    API.put('/site-config', {
      speakUpSettings: { enabled, intro },
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Changes Saved' })
        setChangesPresent(false)
        setOrgConfig((prev) => ({ ...prev, orgSettings: res.data }))
      }
    })
  }

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="title2" fontWeight={700}>
          Freedom to Speak Up
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Lets any member of staff raise a concern — anonymously by default —
          separately from incident reporting. Concerns go to the Governance
          team.
        </Typography>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Stack>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Enable Freedom to Speak Up"
          subheader="When enabled, staff see a Speak Up option on the web and mobile apps and can raise concerns anonymously."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked)
                  setChangesPresent(true)
                }}
              />
            }
            label={
              enabled
                ? 'Freedom to Speak Up is enabled'
                : 'Freedom to Speak Up is disabled'
            }
          />
          {!enabled && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Staff will not be able to raise concerns through AmbuNet. The NHS
              and CQC expect every provider to give workers a route to speak up
              — make sure an alternative is in place and communicated.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Guidance shown to staff"
          subheader="Optional. Replaces the default introduction on the Speak Up form — a good place for your Freedom to Speak Up Guardian's name, or external helplines such as the National Guardian's Office or Protect."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={intro}
            onChange={(e) => {
              setIntro(e.target.value)
              setChangesPresent(true)
            }}
            placeholder="e.g. Our Freedom to Speak Up Guardian is Jo Bloggs (jo.bloggs@example.org). You can also call Protect on 020 3117 2520 for confidential advice."
          />
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="How anonymity works"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            When a concern is raised anonymously, AmbuNet does not store the
            reporter's account, name or any activity log entry for the
            submission. There is no administrator view that can reveal it. If a
            reporter chooses to share their name, it is shown only to the
            Governance team on the concern itself.
          </Typography>
        </CardContent>
      </Card>

      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 2,
          borderRadius: 3,
          background: '#ffffffcc',
          backdropFilter: 'blur(10px)',
          border: '1px solid #eee',
        }}
      >
        <Button
          size="large"
          variant="contained"
          fullWidth
          color="success"
          onClick={handleSaveChanges}
          disabled={!changesPresent}
        >
          Save Changes
        </Button>
      </Paper>
    </Stack>
  )
}

export default SpeakUpSettings
